<template>
  <v-card>
    <div class="card-top">
      <div class="flex space-between w-full">
        <router-link :to="path">
          <h2 class="card-top__title">{{ item.name | shortstr }}</h2>
        </router-link>
        <v-btn :disabled="isConsultant" icon @click="onSelect">
          <v-icon font-size="1" icon="cog"></v-icon>
        </v-btn>
      </div>
    </div>
    <div class="card-content">
      <!-- Total loadings (single block with color logic) -->
      <div class="card-wrapper" :class="getTotalLoadingColorClass()">
        <i class="i-card-tractor mr-2"></i>
        <span class="card-content__info">{{ 'base.loading_cnt' | translate }}</span>
        <span class="card-content__amount">{{ totalLoadingsDone }} / {{ totalLoadingsPlanned }}</span>
      </div>

      <!-- Delay status for each period -->
      <div v-for="loading in item.loadings" :key="`delay-${item.id}-${loading.name}`" class="card-wrapper"
        :class="getDelayColorClass(loading)">
        <i class="i-card-time mr-2"></i>
        <span class="card-content__info">
          {{ 'activities.delay' | translate }}: {{ loading.name }}
        </span>
        <span class="card-content__amount">
          {{ getDelayAmount(loading) }}
        </span>
      </div>

      <!-- Animal count (no color) -->
      <div class="card-wrapper">
        <i class="i-card-cow mr-2"></i>
        <span class="card-content__info">{{ 'base.animal_cnt' | translate }}</span>
        <span class="card-content__amount">{{ item.total_animal_count }}</span>
      </div>

      <!-- Leftovers (no color) -->
      <div class="card-wrapper">
        <i class="i-card-cow mr-2"></i>
        <span class="card-content__info">{{ 'base.leftovers' | translate }}</span>
        <span class="card-content__amount">
          {{ item.leftovers.total_fact_leftover | kgtotn }} {{ 'base.t' | translate }} / {{
            item.leftovers.total_fact_percent }}%
        </span>
      </div>

      <!-- Feed intake (no color) -->
      <!-- <div class="card-wrapper">
        <i class="i-card-panel mr-2"></i>
        <span class="card-content__info">{{ 'base.feed_inout' | translate }}</span>
        <span class="card-content__amount">{{ item.daily_feed_intake | kgtotn }} {{ 'base.t' | translate }}</span>
      </div> -->
    </div>
  </v-card>
</template>

<script>
import adaptationMixin from '@/mixins/adaptation.mixin.js'
import { getFarmPath } from '@/util/app.js'
export default {
  name: 'FarmCardItem',

  mixins: [adaptationMixin],

  props: {
    item: {
      type: Object,
      required: true
    }
  },

  computed: {
    path () {
      this.$store.dispatch('setClientId', this.$route.params.client_id)
      return getFarmPath(this.item, this.$route)
    },

    totalLoadingsDone () {
      return this.item.loadings.reduce((sum, loading) => sum + loading.total_done, 0)
    },

    totalLoadingsPlanned () {
      return this.item.loadings.reduce((sum, loading) => sum + loading.total_planned, 0)
    }
  },

  methods: {
    onSelect () {
      this.$emit('select', this.item)
    },

    getTotalLoadingColorClass () {
      const firstPeriod = this.item.loadings.find(loading => loading.ordering === 1)
      if (firstPeriod && !this.isLoadingPeriodCompleted(firstPeriod)) {
        return 'card-wrapper--white'
      }

      // Get total values
      const totalDone = this.totalLoadingsDone
      const totalPlanned = this.totalLoadingsPlanned

      // Red if after completion of any loading period, total done is still 0
      const hasCompletedPeriodWithZeroLoadings = this.item.loadings.some(loading =>
        this.isLoadingPeriodCompleted(loading)
      )

      if (hasCompletedPeriodWithZeroLoadings && totalDone === 0) {
        return 'card-wrapper--red'
      }

      // Yellow if after completion of any loading period, not all loadings are done
      // (i.e., we have some loadings but not all planned)
      if (hasCompletedPeriodWithZeroLoadings && totalDone > 0 && totalDone < totalPlanned) {
        return 'card-wrapper--yellow'
      }

      // Default white color
      return 'card-wrapper--white'
    },

    getDelayColorClass (loading) {
      // 1. Red if loading started after scheduled end time
      if (this.isLoadingStartedAfterEnd(loading)) {
        return 'card-wrapper--red'
      }

      // 2. Red if period not started but end time passed
      if (this.isPeriodEndOverdue(loading)) {
        return 'card-wrapper--red'
      }

      // 3. Red if started but no loadings done and start time passed
      if (this.isPeriodOverdue(loading)) {
        return 'card-wrapper--red'
      }

      return 'card-wrapper--white'
    },

    isLoadingStartedAfterEnd (loading) {
      if (!loading.started_at) return false

      try {
        const startedAtUTC = new Date(loading.started_at)

        // Convert UTC to farm timezone
        const farmTimeString = new Intl.DateTimeFormat('sv-SE', {
          timeZone: this.item.timezone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        }).format(startedAtUTC)

        const farmTime = new Date(farmTimeString)
        const farmDate = new Date(farmTime.getFullYear(), farmTime.getMonth(), farmTime.getDate())

        // Create scheduled end time
        const [toHours, toMinutes] = loading.loading_time_to.split(':')
        const scheduledEnd = new Date(farmDate)
        scheduledEnd.setHours(parseInt(toHours), parseInt(toMinutes), 0, 0)

        // If started after scheduled end, show red
        return farmTime > scheduledEnd
      } catch (error) {
        console.error('Error checking if loading started after end:', error)
        return false
      }
    },

    getDelayAmount (loading) {
      try {
        const now = new Date()

        // If loading has started, calculate delay from started_at to loading_time_to
        if (loading.started_at) {
          const startedAtUTC = new Date(loading.started_at)

          // Convert UTC time to farm timezone using Intl.DateTimeFormat
          const farmTimeString = new Intl.DateTimeFormat('sv-SE', {
            timeZone: this.item.timezone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          }).format(startedAtUTC)

          const farmTime = new Date(farmTimeString)

          // Create scheduled end time on the same date as farm time
          const farmDate = new Date(farmTime.getFullYear(), farmTime.getMonth(), farmTime.getDate())
          const [toHours, toMinutes] = loading.loading_time_to.split(':')
          const scheduledEnd = new Date(farmDate)
          scheduledEnd.setHours(parseInt(toHours), parseInt(toMinutes), 0, 0)

          // Calculate delay: farm time minus scheduled end time
          if (farmTime > scheduledEnd) {
            const delayMs = farmTime - scheduledEnd
            const delayHours = Math.floor(delayMs / (1000 * 60 * 60))
            const delayMinutes = Math.floor((delayMs % (1000 * 60 * 60)) / (1000 * 60))

            return `${String(delayHours).padStart(2, '0')}:${String(delayMinutes).padStart(2, '0')}`
          }

          return '00:00'
        }

        // If loading hasn't started yet (started_at: null)
        const nowInFarmTz = new Date(now.toLocaleString('sv-SE', { timeZone: this.item.timezone }))
        const today = new Date(nowInFarmTz.getFullYear(), nowInFarmTz.getMonth(), nowInFarmTz.getDate())

        const [toHours, toMinutes] = loading.loading_time_to.split(':')
        const scheduledEnd = new Date(today)
        scheduledEnd.setHours(parseInt(toHours), parseInt(toMinutes), 0, 0)

        // If current time is past scheduled end time, show delay dynamically
        if (nowInFarmTz > scheduledEnd) {
          const delayMs = nowInFarmTz - scheduledEnd
          const delayHours = Math.floor(delayMs / (1000 * 60 * 60))
          const delayMinutes = Math.floor((delayMs % (1000 * 60 * 60)) / (1000 * 60))
          return `${String(delayHours).padStart(2, '0')}:${String(delayMinutes).padStart(2, '0')}`
        }

        // If within time window or before start time
        return '00:00'
      } catch (error) {
        console.error('Error calculating delay:', error)
        return '00:00'
      }
    },

    isPeriodCompleted (loading) {
      if (!loading.started_at) return false

      try {
        const now = new Date()
        const startDate = new Date(loading.started_at)

        // Convert to farm timezone
        const nowInFarmTz = new Intl.DateTimeFormat('sv-SE', {
          timeZone: this.item.timezone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        }).format(now)

        const startDateInFarmTz = new Intl.DateTimeFormat('sv-SE', {
          timeZone: this.item.timezone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        }).format(startDate)

        const currentTime = new Date(nowInFarmTz)
        const baseDate = new Date(startDateInFarmTz.split(' ')[0])

        // Create period end time
        const [hours, minutes] = loading.loading_time_to.split(':')
        const endTime = new Date(baseDate)
        endTime.setHours(parseInt(hours), parseInt(minutes), 0, 0)

        return currentTime > endTime
      } catch (error) {
        console.error('Error checking period completion:', error)
        return false
      }
    },

    isPeriodOverdue (loading) {
      if (!loading.started_at) return false

      try {
        const now = new Date()
        const startDate = new Date(loading.started_at)

        const nowInFarmTz = new Intl.DateTimeFormat('sv-SE', {
          timeZone: this.item.timezone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        }).format(now)

        const startDateInFarmTz = new Intl.DateTimeFormat('sv-SE', {
          timeZone: this.item.timezone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        }).format(startDate)

        const currentTime = new Date(nowInFarmTz)
        const baseDate = new Date(startDateInFarmTz.split(' ')[0])

        const [hours, minutes] = loading.loading_time_from.split(':')
        const scheduledStart = new Date(baseDate)
        scheduledStart.setHours(parseInt(hours), parseInt(minutes), 0, 0)

        return currentTime > scheduledStart && loading.total_done === 0
      } catch (error) {
        console.error('Error checking period overdue:', error)
        return false
      }
    },

    isPeriodEndOverdue (loading) {
      // Check if loading hasn't started and current time is past end time
      if (loading.started_at !== null) return false

      try {
        const now = new Date()

        const nowInFarmTz = new Intl.DateTimeFormat('sv-SE', {
          timeZone: this.item.timezone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        }).format(now)

        const currentTime = new Date(nowInFarmTz)
        const today = new Date(nowInFarmTz.split(' ')[0])

        const [hours, minutes] = loading.loading_time_to.split(':')
        const scheduledEnd = new Date(today)
        scheduledEnd.setHours(parseInt(hours), parseInt(minutes), 0, 0)

        return currentTime > scheduledEnd
      } catch (error) {
        console.error('Error checking period end overdue:', error)
        return false
      }
    },

    isLoadingPeriodCompleted (loading) {
      try {
        const now = new Date()

        // Convert current time to farm timezone
        const nowInFarmTz = new Intl.DateTimeFormat('sv-SE', {
          timeZone: this.item.timezone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        }).format(now)

        const currentTime = new Date(nowInFarmTz)
        const today = new Date(nowInFarmTz.split(' ')[0])

        // Create loading period end time (loading_time_to)
        const [hours, minutes] = loading.loading_time_to.split(':')
        const loadingEndTime = new Date(today)
        loadingEndTime.setHours(parseInt(hours), parseInt(minutes), 0, 0)

        // Loading period is completed if current time is past loading_time_to
        return currentTime > loadingEndTime
      } catch (error) {
        console.error('Error checking loading period completion:', error)
        return false
      }
    },

    formatTime (timeString) {
      return timeString.substring(0, 5)
    }
  }
}
</script>

<style scoped>
.card-content {
  padding-top: 1rem;
  padding-right: 0px;
  padding-bottom: 2.5rem;
  padding-left: 0;
}

.card-wrapper--white {
  background-color: white;
}

.card-wrapper--red {
  background-color: #FF9191;
}

.card-wrapper--yellow {
  background-color: #FBE268;
}

.card-wrapper {
  padding: 10px 12px;
  transition: background-color 0.3s ease;
}
</style>